import React from "react";
import CodeBlock from "@/components/CodeBlock";

const DomainDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        CLI Command — add domain
      </h2>

      <p className="mb-6">
        The <strong>add domain</strong> command generates a new Domain entity
        inside the project's Domain layer using a predefined template. It is one
        of the core building blocks of the AxisNet scaffolding system and is
        frequently triggered directly or indirectly via <code>add feature</code>
        .
      </p>

      {/* BASIC USAGE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Basic Usage
        </h3>

        <CodeBlock code={`axisnet add domain --name Employee`} />

        <p className="mt-4 mb-3">
          This command generates the following file under your Domain layer:
        </p>

        <CodeBlock
          language="csharp"
          code={`using AxisNet.Domain;

namespace {{ProjectName}}.Domain;

public class Employee : Entity<Guid> {}
`}
        />

        {/* ADD CONFIGURATION SUPPORT */}
        <h4 className="text-xl font-semibold text-white mt-8 mb-3">
          Optional: Add EF Core Configuration File
        </h4>

        <p className="mb-3">
          If you want the CLI to also generate a matching Entity Framework
          configuration file (e.g. <code>EmployeeConfiguration.cs</code>), you
          can use the <strong>--configuration</strong> flag:
        </p>

        <CodeBlock
          code={`axisnet add domain --name Employee --configuration`}
        />

        <p className="mt-4">
          When this flag is used, the CLI will additionally generate:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>
            A full EF Core configuration class under the Infrastructure layer
          </li>
          <li>
            Default column mappings for primitive properties (if auto-detection
            is enabled)
          </li>
          <li>
            Automatic registration into <code>ModelBuilder</code> if
            <code> autofix-dbcontext</code> is active
          </li>
          <li>
            A consistent file naming pattern following AxisNet conventions
          </li>
        </ul>
      </div>

      {/* FEATURE TRIGGER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Automatic Trigger via add feature
        </h3>

        <p className="mb-4">
          The domain generator is automatically executed when using the{" "}
          <code>add feature</code> command with the <code>--domain</code> flag:
        </p>

        <CodeBlock code={`axisnet add feature --name Employees --domain`} />

        <p className="mt-4">
          In this case, AxisNet generates a full feature module including:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Domain entity file</li>
          <li>Domain mappings (if included in the template)</li>
          <li>
            Repository interface & implementation (if <code>--repository</code>{" "}
            is used)
          </li>
          <li>
            API controller (if <code>--controller</code> is used)
          </li>
          <li>
            Tests (if <code>--test</code> is used)
          </li>
        </ul>
      </div>

      {/* TEMPLATE HANDLING */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          How It Works — Template Processing
        </h3>

        <p className="mb-4">
          The <strong>DomainGenerator</strong> reads the{" "}
          <code>DomainTemplate.txt</code> file and replaces template tokens:
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>
            <code>{`{{ DomainName }}`}</code> → Employee
          </li>
          <li>
            <code>{`{{ ProjectName }}`}</code> → Read automatically from
            axisnet.json
          </li>
        </ul>

        <p>
          After replacement, the processed output is written to the Domain layer
          path defined in <code>ProjectContext.LayerDomainPath</code>.
        </p>
      </div>

      {/* AUTO DB CONTEXT REGISTRATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          AutoFix Mode — Automatic DbContext Integration
        </h3>

        <p className="mb-4">
          When the <code>autofix-dbcontext</code> flag is enabled, the CLI will
          automatically register the new Domain entity inside{" "}
          <code>ApplicationDbContext</code>.
        </p>

        <CodeBlock
          language="csharp"
          code={`public DbSet<Employee> Employees { get; set; }`}
        />

        <p className="mt-4">
          This insertion is performed using{" "}
          <code>BaseGenerator.FindFileAndInsertLine</code> and ensures the new
          entity becomes available for EF Core:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Migrations</li>
          <li>Database Context</li>
          <li>Relationship Mapping</li>
          <li>Querying Support</li>
        </ul>
      </div>

      {/* OVERWRITE BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Overwrite Protection
        </h3>

        <p className="mb-4">
          If a file with the same name already exists, the behavior depends on:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>confirm-overwrite flag OFF:</strong> File is overwritten
            without asking.
          </li>
          <li>
            <strong>confirm-overwrite flag ON:</strong> CLI asks whether to
            overwrite the file.
          </li>
        </ul>
      </div>
    </section>
  );
};

export default DomainDoc;
